import React, { useCallback, useEffect, useState } from 'react';
import {
  getAppReleases,
  getAppVersions,
  createAppRelease,
  updateAppRelease,
  deleteAppRelease,
  AppVersion,
  AppVersionRelease,
  AppPlatform,
} from '../services/api';
import './AdminAppVersions.css';

const VERSION_REGEX = /^\d+\.\d+\.\d+$/;

const emptyForm = {
  version: '',
  is_force_update: true,
  update_message: '',
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const AdminAppVersions: React.FC = () => {
  const [platform, setPlatform] = useState<AppPlatform>('android');
  const [releases, setReleases] = useState<AppVersionRelease[]>([]);
  const [platformConfig, setPlatformConfig] = useState<AppVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingVersion, setDeletingVersion] = useState<string | null>(null);
  const [newRelease, setNewRelease] = useState(emptyForm);
  const [savingVersion, setSavingVersion] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const [releasesRes, versionsRes] = await Promise.all([
        getAppReleases(platform),
        getAppVersions(),
      ]);
      setReleases(releasesRes.data);
      setPlatformConfig(versionsRes.data.find((v) => v.platform === platform) || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load app versions');
    } finally {
      setLoading(false);
    }
  }, [platform]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    const version = newRelease.version.trim();
    if (!VERSION_REGEX.test(version)) {
      setError('Version must be in X.Y.Z format (e.g. 7.0.1)');
      return;
    }
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      await createAppRelease({
        platform,
        version,
        is_force_update: newRelease.is_force_update,
        update_message: newRelease.update_message.trim() || undefined,
      });
      setNewRelease(emptyForm);
      setSuccess(`Release ${version} added for ${platform}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add release');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (release: AppVersionRelease) => {
    if (
      !window.confirm(
        `Delete release ${release.version} for ${platform}? This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingVersion(release.version);
    setError('');
    setSuccess('');
    try {
      await deleteAppRelease(platform, release.version);
      setSuccess(`Release ${release.version} deleted.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete release');
    } finally {
      setDeletingVersion(null);
    }
  };

  const preventEnterSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const latestReleaseVersion = releases[0]?.version ?? null;

  const handleSaveRow = async (
    release: AppVersionRelease,
    draft: { is_force_update: boolean; update_message: string }
  ) => {
    setSavingVersion(release.version);
    setError('');
    setSuccess('');
    try {
      await updateAppRelease(platform, release.version, {
        is_force_update: draft.is_force_update,
        update_message: draft.update_message.trim() || undefined,
      });
      setSuccess(`Release ${release.version} updated.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update release');
    } finally {
      setSavingVersion(null);
    }
  };

  if (loading && releases.length === 0 && !platformConfig) {
    return <div className="admin-app-versions loading">Loading force update settings…</div>;
  }

  return (
    <div className="admin-app-versions">
      <p className="admin-app-versions-intro">
        Manage <strong>force updates</strong> for the mobile app. Add a release when you ship a new
        version to the store. If marked as force, users on older versions will be blocked until they
        update. A force flag on any release between the user&apos;s version and the latest forces the
        update.
      </p>

      <div className="admin-app-versions-tabs">
        <button
          type="button"
          className={`admin-app-versions-tab ${platform === 'android' ? 'active' : ''}`}
          onClick={() => setPlatform('android')}
        >
          Android
        </button>
        <button
          type="button"
          className={`admin-app-versions-tab ${platform === 'ios' ? 'active' : ''}`}
          onClick={() => setPlatform('ios')}
        >
          iOS
        </button>
      </div>

      {error && <div className="admin-app-versions-error">{error}</div>}
      {success && <div className="admin-app-versions-success">{success}</div>}

      {platformConfig && (
        <section className="admin-app-versions-card">
          <h2>{platform === 'ios' ? 'iOS' : 'Android'} — current config</h2>
          <div className="admin-app-versions-summary">
            <span className="admin-app-versions-summary-item">
              Latest version: <strong>{platformConfig.latest_version}</strong>
            </span>
            <span className="admin-app-versions-summary-item">
              Min supported: <strong>{platformConfig.min_supported_version}</strong>
            </span>
            {platformConfig.store_url && (
              <span className="admin-app-versions-summary-item">
                Store URL:{' '}
                <a href={platformConfig.store_url} target="_blank" rel="noopener noreferrer">
                  {platformConfig.store_url}
                </a>
              </span>
            )}
          </div>
        </section>
      )}

      <section className="admin-app-versions-card">
        <h2>Add release — {platform === 'ios' ? 'iOS' : 'Android'}</h2>
        <div className="admin-app-versions-form">
          <div className="admin-app-versions-form-fields">
            <label>
              <span>Version</span>
              <input
                value={newRelease.version}
                onChange={(e) => setNewRelease((s) => ({ ...s, version: e.target.value }))}
                onKeyDown={preventEnterSubmit}
                placeholder="e.g. 7.0.1"
              />
            </label>
            <label>
              <span>Update message</span>
              <textarea
                value={newRelease.update_message}
                onChange={(e) => setNewRelease((s) => ({ ...s, update_message: e.target.value }))}
                onKeyDown={preventEnterSubmit}
                placeholder="Message shown to users on the force update screen"
                rows={2}
              />
            </label>
          </div>
          <div className="admin-app-versions-form-actions">
            <label className="force-toggle">
              <input
                type="checkbox"
                checked={newRelease.is_force_update}
                onChange={(e) => setNewRelease((s) => ({ ...s, is_force_update: e.target.checked }))}
              />
              Force update
            </label>
            <button
              type="button"
              className="btn-add-release"
              disabled={creating}
              onClick={handleCreate}
            >
              {creating ? 'Saving…' : 'Save release'}
            </button>
          </div>
        </div>
      </section>

      <section className="admin-app-versions-card">
        <h2>Release history — {platform === 'ios' ? 'iOS' : 'Android'}</h2>
        <div className="admin-app-versions-table-wrap">
          <table className="admin-app-versions-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Type</th>
                <th>Message</th>
                <th>Force</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {releases.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty">
                    No releases yet for {platform}.
                  </td>
                </tr>
              ) : (
                releases.map((release) => (
                  <ReleaseEditRow
                    key={release.id}
                    release={release}
                    saving={savingVersion === release.version}
                    deleting={deletingVersion === release.version}
                    isLatest={release.version === latestReleaseVersion}
                    onSave={handleSaveRow}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

interface ReleaseEditRowProps {
  release: AppVersionRelease;
  saving: boolean;
  deleting: boolean;
  isLatest: boolean;
  onSave: (
    release: AppVersionRelease,
    draft: { is_force_update: boolean; update_message: string }
  ) => void;
  onDelete: (release: AppVersionRelease) => void;
}

const ReleaseEditRow: React.FC<ReleaseEditRowProps> = ({
  release,
  saving,
  deleting,
  isLatest,
  onSave,
  onDelete,
}) => {
  const [isForce, setIsForce] = useState(release.is_force_update === 1);
  const [message, setMessage] = useState(release.update_message || '');

  useEffect(() => {
    setIsForce(release.is_force_update === 1);
    setMessage(release.update_message || '');
  }, [release]);

  const dirty =
    isForce !== (release.is_force_update === 1) ||
    message !== (release.update_message || '');

  return (
    <tr>
      <td>
        <strong>{release.version}</strong>
      </td>
      <td>
        <span className={`force-badge ${release.is_force_update === 1 ? 'force' : 'soft'}`}>
          {release.is_force_update === 1 ? 'Force' : 'Soft'}
        </span>
      </td>
      <td>
        <input
          className="cell-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          placeholder="Update message"
        />
      </td>
      <td>
        <div className="cell-checkbox">
          <input
            type="checkbox"
            checked={isForce}
            onChange={(e) => setIsForce(e.target.checked)}
            title="Force update"
          />
        </div>
      </td>
      <td className="admin-app-versions-date">{formatDate(release.created_at)}</td>
      <td>
        <div className="admin-app-versions-row-actions">
          <button
            type="button"
            className="btn-save-row"
            disabled={!dirty || saving || deleting}
            onClick={() => onSave(release, { is_force_update: isForce, update_message: message })}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {isLatest && (
            <button
              type="button"
              className="btn-delete-row"
              disabled={saving || deleting}
              onClick={() => onDelete(release)}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default AdminAppVersions;
